import {
  type Canvas,
  FabricObject,
  type SerializedImageProps,
  type SerializedObjectProps,
} from "fabric";
import type { RgbaColor } from "../../../../../../types.ts";
import {
  ASSET_LAYER_TYPE,
  CIRCLE_SHAPE_TYPE,
  IMAGE_LAYER_TYPE,
  LINE_LAYER_TYPE,
  RECT_SHAPE_TYPE,
  SHAPE_LAYER_TYPE,
  TEXT_LAYER_TYPE,
  TRIANGLE_SHAPE_TYPE,
} from "../constants.ts";
import {
  type DesignerCanvasState,
  designerCanvasStore,
} from "../stores/designerCanvasStore.ts";
import { fabricObjectToLayer } from "../typeguard.ts";
import type { CanvasSize, Layer } from "../types.ts";
import { addLineLayerEventListeners } from "./line.ts";
import {
  addCircleShapeLayerEventListeners,
  addShapeLayerEventListeners,
} from "./shape.ts";
import { lockTextLayerScaling } from "./text.ts";

// XXX: We override the toObject method to include the id and name properties in the JSON representation of the object.
// This can be extended to include other properties in the future when supporting more layer types.

const originalToObject = FabricObject.prototype.toObject;

FabricObject.prototype.toObject = function (
  this: FabricObject,
  propertiesToInclude?: string[],
) {
  const extraProps = [
    "id",
    "name",
    "positionIndex",
    "layerType",
    "guideLines",
    "shapeType",
    "strokeStyle",
    "contentUuid",
    "assetUuid",
    "shadowStyle",
    "shadowDirection",
    "shadowDistance",
    "displayScale",
    "targetWidth",
    "targetHeight",
  ];

  return originalToObject.call(
    this,
    extraProps.concat(propertiesToInclude ?? []),
  );
};

export const serializeCanvas = ({
  fabricCanvas,
  canvasState,
}: {
  fabricCanvas: Canvas;
  canvasState: {
    isDrawingMode: boolean;
    isDeletionDisabled: boolean;
    selectedLayerIds: string[];
    backgroundColor: RgbaColor | undefined;
    canvasSize: CanvasSize | undefined;
  };
}) => {
  const {
    canvasSize,
    backgroundColor,
    isDrawingMode,
    isDeletionDisabled,
    selectedLayerIds,
  } = canvasState;

  if (!canvasSize) return undefined;

  const canvasData = fabricCanvas.toJSON();
  canvasData.objects = canvasData.objects
    .map((object: SerializedObjectProps) => {
      if (
        (object as any)?.layerType === IMAGE_LAYER_TYPE ||
        (object as any)?.layerType === ASSET_LAYER_TYPE
      ) {
        // XXX: We exclude image layers data from the serialization to avoid memory issues with local storage.
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { src, ...objectWithoutSrc } = object as SerializedImageProps;
        return objectWithoutSrc;
      } else {
        return object;
      }
    })
    .map((object: SerializedObjectProps | null) => {
      if (!object) return null;
      // XXX: We can safely cast to FabricObject here as Fabric serialized objects have the same properties as FabricObject.
      // XXX: We filter out `Fabric.js` internal objects and temporary objects that lack layer IDs.
      // Only user-created layer objects should have IDs and be persisted to localStorage.
      return fabricObjectToLayer(object as FabricObject);
    })
    .filter((layer: Layer | null) => layer !== null);

  return {
    canvasData,
    isDrawingMode,
    isDeletionDisabled,
    selectedLayerIds,
    backgroundColor,
    canvasSize,
  };
};

export const deserializeCanvas = async ({
  saved,
  fabricCanvas,
  setCanvasState,
}: {
  saved: any;
  fabricCanvas: Canvas;
  setCanvasState: (state: Partial<DesignerCanvasState>) => void;
}): Promise<void> => {
  // FIXME: Replace fabricCanvas.loadFromJSON() with layer-by-layer restoration using our `createLayer` functions.
  // Indeed, JSON loading doesn't restore custom properties/behaviors properly, and requires manual cleanup
  // of internal Fabric objects, forcing us to re-add event listeners afterward. A proper restoration would:
  // 1. Read layer data from `saved.canvasData.objects`
  // 2. Call appropriate `createLayer` functions (createTextLayer, createShapeLayer, etc.) for each layer
  // 3. Automatically restore all custom properties, event listeners, and behaviors
  // 4. Eliminate the need for post-restoration cleanup and manual event listener restoration
  await fabricCanvas.loadFromJSON(saved.canvasData);

  if (saved.backgroundColor) {
    designerCanvasStore.setBackgroundColor(saved.backgroundColor);
  }

  if (saved.canvasSize) designerCanvasStore.setCanvasSize(saved.canvasSize);
  else return;

  const restoredObjects = fabricCanvas.getObjects();
  const restoredLayers: Layer[] = [];
  for (const object of restoredObjects) {
    const layer = fabricObjectToLayer(object);
    // XXX: We filter out `Fabric.js` internal objects and temporary objects that lack layer IDs, to restore only user-created layers.
    if (layer) {
      restoredLayers.push(layer);
    } else {
      fabricCanvas.remove(object);
    }
  }

  // XXX: Event listeners need to dynanically restored for each layer since they are not serialized.
  for (const layer of restoredLayers) {
    restoreLayer(layer, fabricCanvas, saved.canvasSize);
  }

  setCanvasState({
    isDrawingMode: saved.isDrawingMode,
    isDeletionDisabled: saved.isDeletionDisabled,
    selectedLayerIds: saved.selectedLayerIds,
    backgroundColor: saved.backgroundColor,
    canvasSize: saved.canvasSize,
  });

  if (saved.selectedLayerIds) {
    const foundLayer = restoredLayers.find((l) =>
      saved.selectedLayerIds.includes(l.id),
    );
    if (foundLayer) {
      const matchingObject = restoredObjects.find(
        (obj) => (obj as any).id === foundLayer.id,
      );
      if (matchingObject) fabricCanvas.setActiveObject(matchingObject);
    }
  }

  fabricCanvas.renderAll();
};

const restoreLayer = (
  layer: Layer,
  fabricCanvas: Canvas,
  canvasSize: CanvasSize,
) => {
  switch (layer.layerType) {
    case TEXT_LAYER_TYPE:
      lockTextLayerScaling(layer);
      break;
    case SHAPE_LAYER_TYPE:
      switch (layer.shapeType) {
        case RECT_SHAPE_TYPE:
          addShapeLayerEventListeners(layer, fabricCanvas, canvasSize);
          break;
        case CIRCLE_SHAPE_TYPE:
          addCircleShapeLayerEventListeners(layer, fabricCanvas, canvasSize);
          break;
        case TRIANGLE_SHAPE_TYPE:
          addShapeLayerEventListeners(layer, fabricCanvas, canvasSize);
          break;
      }
      break;
    case LINE_LAYER_TYPE:
      addLineLayerEventListeners(layer, fabricCanvas, canvasSize);
      break;
    case IMAGE_LAYER_TYPE:
    case ASSET_LAYER_TYPE:
      break;
  }
};
